import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { CheckCircle2, AlertCircle, Info } from 'lucide-react'

type ToastTone = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  tone: ToastTone
  message: string
}

interface ToastContextValue {
  show: (tone: ToastTone, message: string) => void
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TOAST_STYLE: Record<ToastTone, { box: string; icon: string; Icon: typeof CheckCircle2 }> = {
  success: {
    box: 'border-green-200 bg-green-50 text-green-800',
    icon: 'text-green-600',
    Icon: CheckCircle2,
  },
  error: {
    box: 'border-red-200 bg-red-50 text-red-800',
    icon: 'text-red-600',
    Icon: AlertCircle,
  },
  info: {
    box: 'border-blue-200 bg-blue-50 text-blue-800',
    icon: 'text-blue-600',
    Icon: Info,
  },
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const counterRef = useRef(1)

  const remove = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const show = useCallback(
    (tone: ToastTone, message: string) => {
      const id = counterRef.current++
      setItems((current) => [...current, { id, tone, message }])
      window.setTimeout(() => remove(id), 3200)
    },
    [remove]
  )

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (message) => show('success', message),
      error: (message) => show('error', message),
      info: (message) => show('info', message),
    }),
    [show]
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-3">
        {items.map((item) => {
          const { box, icon, Icon } = TOAST_STYLE[item.tone]
          return (
            <div
              key={item.id}
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg ${box}`}
            >
              <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${icon}`} />
              <p className="text-sm font-medium">{item.message}</p>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}
